import { useSyncExternalStore } from 'react';

// Общее состояние «что сейчас играет» для всей истории карточки лида.
// Элемент воспроизведения один на всю историю: это и есть гарантия того, что
// одновременно играет только одно аудио (требование us/10772), и точка, к
// которой позже подключится закреплённый плеер (task/10773).
//
// Audio() здесь — медиа-контроллер, а не React-управляемая разметка: элемент
// в документ не вставляется и ничего не рендерит.

export interface LeadAudioPlaybackState {
  fileId: number | null;
  // Лид, из истории которого запущена запись. Карточка лида умеет открываться
  // вложенно (модалка записи, ClientCard), поэтому при размонтировании одной
  // истории нельзя глушить запись, запущенную из другой.
  leadId: number | null;
  // Сообщение, которому принадлежит запись: по нему закреплённый плеер
  // прокручивает историю к исходному сообщению.
  messageId: number | null;
  title: string;
  isIncoming: boolean;
  durationSec: number;
  positionSec: number;
  isPlaying: boolean;
  speed: number;
  // Счётчик неудачных попыток воспроизведения текущей записи. Растёт на
  // каждой ошибке: по его изменению плеер показывает уведомление. Само
  // состояние ошибкой не сбрасывается — иначе некому было бы сообщить.
  errorCount: number;
}

export interface LeadAudioTrack {
  fileId: number;
  leadId: number | null;
  messageId: number | null;
  title: string;
  isIncoming: boolean;
  url: string;
  durationSec: number;
}

export const LEAD_AUDIO_SPEEDS = [1, 1.5, 2];

const initialState: LeadAudioPlaybackState = {
  fileId: null,
  leadId: null,
  messageId: null,
  title: '',
  isIncoming: true,
  durationSec: 0,
  positionSec: 0,
  isPlaying: false,
  speed: 1,
  errorCount: 0,
};

let state = initialState;
let audio: HTMLAudioElement | null = null;

const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const patch = (next: Partial<LeadAudioPlaybackState>) => {
  state = { ...state, ...next };
  emit();
};

const ensureAudio = (): HTMLAudioElement | null => {
  if (typeof Audio === 'undefined') return null;
  if (audio) return audio;

  audio = new Audio();

  audio.addEventListener('timeupdate', () => {
    patch({ positionSec: audio?.currentTime ?? 0 });
  });
  audio.addEventListener('loadedmetadata', () => {
    const duration = audio?.duration ?? 0;
    if (Number.isFinite(duration) && duration > 0)
      patch({ durationSec: duration });
  });
  audio.addEventListener('play', () => patch({ isPlaying: true }));
  audio.addEventListener('pause', () => patch({ isPlaying: false }));
  audio.addEventListener('ended', () =>
    patch({ isPlaying: false, positionSec: 0 })
  );
  // Битый или неподдерживаемый браузером файл (например .amr в Chrome) не
  // бросает исключение из play(): ошибка приходит событием. Без этого закреп
  // висел бы с записью, которую нельзя проиграть, и молча.
  audio.addEventListener('error', () => {
    patch({ isPlaying: false, errorCount: state.errorCount + 1 });
  });

  return audio;
};

export const leadAudioPlayback = {
  subscribe(listener: () => void) {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  },

  get(): LeadAudioPlaybackState {
    return state;
  },

  // Запускает трек. Если играет другой — он останавливается: элемент один.
  async play(track: LeadAudioTrack): Promise<boolean> {
    const element = ensureAudio();
    if (!element) return false;

    if (state.fileId !== track.fileId) {
      element.src = track.url;
      element.currentTime = 0;
      patch({
        fileId: track.fileId,
        leadId: track.leadId,
        messageId: track.messageId,
        title: track.title,
        isIncoming: track.isIncoming,
        durationSec: track.durationSec,
        positionSec: 0,
      });
    }

    // playbackRate сбрасывается на defaultPlaybackRate при смене src, поэтому
    // задаём обе величины: иначе новая запись играет на 1x, а подпись в
    // закрепе показывает выбранную скорость.
    element.defaultPlaybackRate = state.speed;
    element.playbackRate = state.speed;

    try {
      await element.play();

      return true;
    } catch {
      patch({ isPlaying: false, errorCount: state.errorCount + 1 });

      return false;
    }
  },

  pause() {
    audio?.pause();
  },

  // Возобновление уже выбранной записи: источник у элемента уже стоит.
  async resume(): Promise<void> {
    if (!audio || state.fileId === null) return;

    audio.playbackRate = state.speed;

    try {
      await audio.play();
    } catch {
      patch({ isPlaying: false });
    }
  },

  seekRatio(ratio: number) {
    const element = audio;
    if (!element) return;

    const duration = state.durationSec;
    if (!duration) return;

    const position = Math.max(0, Math.min(1, ratio)) * duration;
    element.currentTime = position;
    patch({ positionSec: position });
  },

  setSpeed(speed: number) {
    if (audio) audio.playbackRate = speed;
    patch({ speed });
  },

  stop() {
    audio?.pause();
    if (audio) audio.currentTime = 0;
    state = {
      ...initialState,
      speed: state.speed,
      errorCount: state.errorCount,
    };
    emit();
  },

  // Снимает воспроизведение, только если играет запись указанного лида.
  // История карточки монтируется и вложенно (карточка лида внутри модалки
  // записи), и её размонтирование не должно глушить запись соседней карточки.
  stopForLead(leadId: number | null) {
    if (state.fileId === null || state.leadId !== leadId) return;
    this.stop();
  },
};

export const useLeadAudioPlayback = (): LeadAudioPlaybackState =>
  useSyncExternalStore(leadAudioPlayback.subscribe, leadAudioPlayback.get);
